import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'
import './data-summary-view.js'

import { CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { PageView, store } from '@operato/shell'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import moment from '@operato/moment-timezone-es'
import { DataGrist, GristRecord, FetchOption } from '@operato/data-grist'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { notify, openPopup } from '@operato/layout'
import { isMobileDevice } from '@operato/utils'

import { connect } from 'pwa-helpers/connect-mixin'
import gql from 'graphql-tag'

@customElement('data-summary-list-page')
export class DataSummaryListPage extends connect(store)(localize(i18next)(PageView)) {
  static styles = [
    ScrollbarStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        overflow: hidden;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() private mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'
  @state() private gristConfig: any

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-summary list'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-summary',
      exportable: {
        name: i18next.t('title.data-summary list'),
        data: this._exportableData.bind(this)
      },
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'LIST' : 'GRID')

    return html`
      <ox-grist
        .mode=${mode}
        .config=${this.gristConfig}
        .fetchHandler=${this.fetchHandler.bind(this)}
        ?url-params-sensitive=${false /* this.active */}
      >
        <div slot="headroom" class="header">
          <div class="filters">
            <ox-filters-form autofocus without-search></ox-filters-form>
          </div>

          <ox-context-page-toolbar class="actions" .context=${this.context}></ox-context-page-toolbar>
        </div>
      </ox-grist>
    `
  }

  async pageInitialized(lifecycle) {
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD')
    const today = moment().format('YYYY-MM-DD')

    this.gristConfig = {
      list: { fields: ['dataSet', 'data', 'updater', 'updatedAt'] },
      columns: [
        { type: 'gutter', gutterName: 'sequence', fixed: true },
        { type: 'gutter', gutterName: 'row-selector', multiple: true, fixed: true },
        {
          type: 'gutter',
          gutterName: 'button',
          fixed: true,
          icon: 'assignment',
          iconOnly: false,
          title: i18next.t('button.detail'),
          width: 72
        },
        {
          type: 'string',
          name: 'name',
          fixed: true,
          label: true,
          header: i18next.t('field.name'),
          record: {
            editable: false
          },
          filter: 'search',
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'description',
          label: true,
          header: i18next.t('field.description'),
          record: {
            editable: false
          },
          filter: 'search',
          width: 150,
          imex: true
        },
        {
          type: 'string',
          name: 'date',
          header: i18next.t('field.date'),
          sortable: true,
          filter: {
            type: 'date',
            operator: 'between',
            value: [yesterday, today]
          },
          width: 80,
          imex: true
        },
        {
          type: 'string',
          name: 'period',
          header: i18next.t('field.time-period'),
          sortable: true,
          width: 60,
          imex: true
        },
        ...Array.from({ length: 5 }, (_, idx) => {
          return {
            type: 'string',
            name: `key0${idx + 1}`,
            header: {
              renderer: i18next.t(`field.key-0${idx + 1}`),
              group: i18next.t('field.key')
            },
            record: {
              editable: false
            },
            width: 120,
            imex: true
          }
        }),
        ...Array.from({ length: 5 }, (_, idx) => {
          return {
            type: 'number',
            name: `data0${idx + 1}`,
            header: {
              renderer: i18next.t(`field.data-0${idx + 1}`),
              group: i18next.t('field.data')
            },
            record: {
              editable: false,
              align: 'right'
            },
            width: 120,
            imex: true
          }
        }),
        {
          type: 'number',
          name: 'count',
          header: i18next.t('field.count'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'number',
          name: 'countOoc',
          header: i18next.t('field.count-ooc'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'number',
          name: 'countOos',
          header: i18next.t('field.count-oos'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'resource-object',
          name: 'updater',
          header: i18next.t('field.updater'),
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'datetime',
          name: 'updatedAt',
          header: i18next.t('field.updated_at'),
          sortable: true,
          width: 180,
          imex: true
        }
      ],
      rows: {
        appendable: false,
        selectable: {
          multiple: true
        },
        classifier: function (record, rowIndex) {
          var emphasized
          if (record['oos']) {
            emphasized = ['#8B0000', 'white']
          } else if (record['ooc']) {
            emphasized = ['#FF6B6B', 'white']
          }

          return {
            emphasized
          }
        },
        handlers: {
          click: (columns, data, column, record, rowIndex) => {
            openPopup(
              html`
                <data-summary-view data-summary-id=${record.id} style="background-color: white;"></data-summary-view>
              `,
              {
                backdrop: true,
                size: 'large',
                title: i18next.t('title.data-summary view')
              }
            )
          }
        }
      },
      sorters: [
        {
          name: 'date',
          desc: true
        }
      ]
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    const response = await client.query({
      query: gql`
        query ($filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!]) {
          responses: dataSummaries(filters: $filters, pagination: $pagination, sortings: $sortings) {
            items {
              id
              name
              description
              dataSet {
                id
              }
              date
              period
              key01
              key02
              key03
              key04
              key05
              data01
              data02
              data03
              data04
              data05
              summary
              count
              countOoc
              countOos
              updater {
                id
                name
              }
              updatedAt
            }
            total
          }
        }
      `,
      variables: {
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      total: response.data.responses.total || 0,
      records: response.data.responses.items || []
    }
  }

  _exportableData() {
    let records = [] as GristRecord[]
    if (this.grist.selected && this.grist.selected.length > 0) {
      records = this.grist.selected
    } else {
      records = this.grist.data.records
    }

    var headerSetting = this.grist.compiledConfig.columns
      .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
      .map(column => {
        return column.imex === true
          ? {
              header: column.header.renderer(column),
              key: column.name,
              width: column.width,
              type: column.type
            }
          : column.imex
      })

    var data = records.map(item => {
      return {
        id: item.id,
        ...this.gristConfig.columns
          .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
          .reduce((record, column) => {
            const key = column.imex === true ? column.name : column.imex.key
            record[key] = key
              .split('.')
              .reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), item)
            return record
          }, {})
      }
    })

    return { header: headerSetting, data: data }
  }
}
